/**
 * Sectors hooks — universal sector picker (Network-iA multi-métier).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api';

export interface SectorCatalogItem {
  key: string;
  label: string;
  emoji: string;
  description: string;
  prospect_label: string;
  client_label: string;
  supports_mlm_link: boolean;
}

export interface UserSectors {
  sectors: string[];
  custom_sector_label: string | null;
}

/** Public catalog — 8 secteurs + "custom" possibility. */
export function useSectorCatalog() {
  return useQuery<SectorCatalogItem[]>({
    queryKey: ['sectors', 'catalog'],
    queryFn: () => apiRequest<SectorCatalogItem[]>('/sectors', { auth: false }),
    staleTime: 5 * 60_000,
  });
}

/** Current user's chosen sectors + custom label. */
export function useUserSectors() {
  return useQuery<UserSectors>({
    queryKey: ['sectors', 'me'],
    queryFn: () => apiRequest<UserSectors>('/profile/sectors'),
    staleTime: 30_000,
  });
}

/** Update user's sectors + optional custom label. */
export function useUpdateUserSectors() {
  const qc = useQueryClient();
  return useMutation<UserSectors, Error, UserSectors>({
    mutationFn: (payload) =>
      apiRequest<UserSectors>('/profile/sectors', {
        method: 'PUT',
        body: payload,
      }),
    onSuccess: (data) => {
      qc.setQueryData(['sectors', 'me'], data);
      // Invalidate Inea-related queries so next call uses fresh sectors.
      qc.invalidateQueries({ queryKey: ['briefing'] });
    },
  });
}
